import { useCallback } from 'react';

import type { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import {
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import type { Row } from '@tanstack/react-table';
import { atom, useSetAtom } from 'jotai';

import { filteredFlatRowsState } from './filtered-flat-rows.state';
import { productsMapState } from './products.state';

// Sentinel selection model: O(1) updates for any selection operation
export type SelectionModel =
  | { mode: 'none' }
  | { mode: 'all'; excluded: Set<string> }
  | { mode: 'some'; included: Set<string> };

export const NONE_SELECTION: SelectionModel = { mode: 'none' };

export const rowSelectionModelState = atom<SelectionModel>(NONE_SELECTION);

// Pure helpers

export function isRowSelected(model: SelectionModel, rowId: string): boolean {
  if (model.mode === 'none') return false;
  if (model.mode === 'all') return !model.excluded.has(rowId);
  return model.included.has(rowId);
}

export function getRowSelectionStatus(
  model: SelectionModel,
  row: Row<DealItemWithVersions>
): boolean | 'indeterminate' {
  if (row.subRows && row.subRows.length > 1) {
    const leafRows = row.getLeafRows();
    if (leafRows.length === 0) return false;
    const allSelected = leafRows.every((lr) =>
      isRowSelected(model, lr.original.id)
    );
    if (allSelected) return true;
    const someSelected = leafRows.some((lr) =>
      isRowSelected(model, lr.original.id)
    );
    return someSelected ? 'indeterminate' : false;
  }
  return isRowSelected(model, row.original.id);
}

export function toggleSingleRow(
  model: SelectionModel,
  rowId: string
): SelectionModel {
  if (model.mode === 'none') {
    return { mode: 'some', included: new Set([rowId]) };
  }
  if (model.mode === 'all') {
    const excluded = new Set(model.excluded);
    if (excluded.has(rowId)) {
      excluded.delete(rowId);
    } else {
      excluded.add(rowId);
    }
    return { mode: 'all', excluded };
  }
  const included = new Set(model.included);
  if (included.has(rowId)) {
    included.delete(rowId);
    if (included.size === 0) return NONE_SELECTION;
  } else {
    included.add(rowId);
  }
  return { mode: 'some', included };
}

export function applyRangeSelection(
  model: SelectionModel,
  rowIds: string[],
  targetState: boolean
): SelectionModel {
  if (targetState) {
    if (model.mode === 'none') {
      return { mode: 'some', included: new Set(rowIds) };
    }
    if (model.mode === 'all') {
      const excluded = new Set(model.excluded);
      rowIds.forEach((id) => excluded.delete(id));
      return { mode: 'all', excluded };
    }
    const included = new Set(model.included);
    rowIds.forEach((id) => included.add(id));
    return { mode: 'some', included };
  } else {
    if (model.mode === 'none') return NONE_SELECTION;
    if (model.mode === 'all') {
      const excluded = new Set(model.excluded);
      rowIds.forEach((id) => excluded.add(id));
      return { mode: 'all', excluded };
    }
    const included = new Set(model.included);
    rowIds.forEach((id) => included.delete(id));
    if (included.size === 0) return NONE_SELECTION;
    return { mode: 'some', included };
  }
}

export function selectAllRows(): SelectionModel {
  return { mode: 'all', excluded: new Set() };
}

export function clearSelection(): SelectionModel {
  return NONE_SELECTION;
}

export const selectedProductsState = atom((get) => {
  const filteredFlatRows = get(filteredFlatRowsState);
  const model = get(rowSelectionModelState);
  const productsMap = get(productsMapState);

  if (model.mode === 'none') return [];

  return filteredFlatRows
    .filter(
      (row) =>
        isRowSelected(model, row.original.id) &&
        (row.subRows?.length === 0 || row.subRows?.length === 1)
    )
    .map((row) => row.original)
    .filter((product) => {
      const latest = resolveDealItemFromMap(productsMap, product);
      return !isDealItemCategoryUpdating(latest);
    });
});

export const selectedProductIdsState = atom((get) => {
  const selectedProducts = get(selectedProductsState);
  return selectedProducts.map((product) => product.id);
});

export const selectedProductsCountState = atom((get) => {
  const selectedProducts = get(selectedProductsState);
  return selectedProducts.length;
});

export const useResetSelection = () => {
  const setModel = useSetAtom(rowSelectionModelState);

  const resetSelection = useCallback(() => {
    setModel(NONE_SELECTION);
  }, [setModel]);

  return { resetSelection };
};
